import React, { useEffect, useState } from "react";
import axios from "axios";
import { Pencil, Trash2, Image as ImageIcon } from 'lucide-react'; // lucide icons

export default function Staffs() {
  const [staffs, setStaffs] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const staffsPerPage = 16;

  useEffect(() => {
    fetchStaffs();
  }, []);

  const fetchStaffs = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getstaff`);
      setStaffs(response.data.data);
    } catch (error) {
      console.error("Error fetching staff:", error);
      alert("Failed to fetch staff.");
    }
  };

  const handleAddStaff = () => setShowAddForm(true);

  const handleEditStaff = (staff) => {
    setEditingStaff(staff);
    setName(staff.name);
    setUsername(staff.username);
    setPassword(""); // Don't populate password for security
    setRole(staff.role);
    setShowEditForm(true);
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setName("");
    setUsername("");
    setPassword("");
    setRole("");
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditingStaff(null);
    setName("");
    setUsername("");
    setPassword("");
    setRole("");
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    if (!name || !username || !password || !role) {
      alert("Please fill out all fields.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/addstaff`, {
        name,
        username,
        password,
        role,
      });

      if (response.status === 200 || response.status === 201) {
        alert("Staff added successfully!");
        setShowAddForm(false);
        setName("");
        setUsername("");
        setPassword("");
        setRole("");
        await fetchStaffs();
      }
    } catch (error) {
      console.error("Error adding staff:", error);
      alert(error.response?.data?.error || "Failed to add staff.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!name || !username || !role) {
      alert("Please fill out all required fields.");
      return;
    }

    setLoading(true);
    try {
      const updateData = {
        originalUsername: editingStaff.username,
        name,
        username,
        role,
      };
      
      // Only include password if it was provided
      if (password && password.trim() !== "") {
        updateData.password = password;
      }

      const response = await axios.put(`${process.env.REACT_APP_API_URL}/admin/editStaff`, updateData);

      if (response.status === 200) {
        alert("Staff updated successfully!");
        setShowEditForm(false);
        setEditingStaff(null);
        setName("");
        setUsername("");
        setPassword("");
        setRole("");
        await fetchStaffs();
      }
    } catch (error) {
      console.error("Error editing staff:", error);
      alert(error.response?.data?.error || "Failed to update staff.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Are you sure you want to delete staff "${username}"?`)) return;
  
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/admin/deleteStaff/${username}`, {
        data: { username }
      });
  
      await fetchStaffs();
      alert("Staff deleted successfully.");
      setCurrentPage(1); // Reset to first page after deletion
    } catch (error) {
      console.error("Error deleting staff:", error);
      alert("Failed to delete staff.");
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
    setCurrentPage(1); // Reset to first page on search
  };

  // Filter staff based on search query
  const filteredStaffs = staffs.filter((staff) =>
    `${staff.username} ${staff.name} ${staff.role}`
      .toLowerCase()
      .includes(searchQuery)
  );

  // Pagination logic
  const indexOfLast = currentPage * staffsPerPage;
  const indexOfFirst = indexOfLast - staffsPerPage;
  const currentStaffs = filteredStaffs.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredStaffs.length / staffsPerPage);

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold">Staffs</h1>
      <div className="bg-neutral-200 p-4"> 
        <div className="mt-2 mb-4 border-t border-gray-400 w-9/12 mx-auto"></div>

        <div className="flex justify-between items-center mb-4">
          <input
            type="text"
            placeholder="Search by username, name, or role"
            value={searchQuery}
            onChange={handleSearch}
            className="w-[300px] px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <button
            onClick={handleAddStaff}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Add Staff
          </button>
        </div>

        <div className="flex flex-row bg-neutral-200 mt-4">
          <div className="flex flex-row w-full">
            <table className="w-full border-collapse bg-white">
              <thead>
                <tr className="bg-gray-200">
                  <th className="border px-4 py-2">Username</th>
                  <th className="border px-4 py-2">Name</th>
                  <th className="border px-4 py-2">Role</th>
                  <th className="border px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {currentStaffs.map((staff) => (
                  <tr key={staff.username} className="text-center border-t hover:bg-gray-50 transition">
                    <td className="px-4 py-2 border">{staff.username}</td>
                    <td className="px-4 py-2 border">{staff.name}</td>
                    <td className="px-4 py-2 border">{staff.role}</td>
                    <td className="px-4 py-2 border">
                      <div className="flex items-center justify-center gap-2">
                        <button 
                          onClick={() => handleEditStaff(staff)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit Staff"
                        >
                          <Pencil size={18} />
                        </button>
                        <button 
                          onClick={() => handleDelete(staff.username)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete Staff"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <select
              value={currentPage}
              onChange={(e) => setCurrentPage(Number(e.target.value))}
              className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <option key={page} value={page}>
                  Page {page} of {totalPages}
                </option>
              ))}
            </select>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}

        {/* Add Staff Modal */}
        {showAddForm && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white w-1/2 max-w-lg rounded-lg shadow-lg p-6 relative">
              <h2 className="text-xl font-semibold mb-4">Add New Staff</h2>
              <form onSubmit={handleSubmitAdd}>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-1">Name:</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="border rounded w-full py-2 px-3"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-1">Username:</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="border rounded w-full py-2 px-3"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-1">Password:</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="border rounded w-full py-2 px-3"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-1">Role:</label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="border rounded w-full py-2 px-3"
                    required
                  >
                    <option value="" disabled>Select role</option>
                    <option value="boss">Boss</option>
                    <option value="super admin">Super Admin</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleCancelAdd}
                    className="bg-gray-500 text-white px-4 py-2 mr-2 rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 ${
                      loading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Staff Modal */}
        {showEditForm && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white w-1/2 max-w-lg rounded-lg shadow-lg p-6 relative">
              <h2 className="text-xl font-semibold mb-4">Edit Staff</h2>
              <form onSubmit={handleSubmitEdit}>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-1">Name:</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="border rounded w-full py-2 px-3"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-1">Username:</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="border rounded w-full py-2 px-3"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-1">
                    Password:
                    <span className="text-sm text-gray-500 ml-2">(Leave blank to keep current password)</span>
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password (optional)"
                    className="border rounded w-full py-2 px-3"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-1">Role:</label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="border rounded w-full py-2 px-3"
                    required
                  >
                    <option value="" disabled>Select role</option>
                    <option value="boss">Boss</option>
                    <option value="super admin">Super Admin</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="bg-gray-500 text-white px-4 py-2 mr-2 rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 ${
                      loading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    disabled={loading}
                  >
                    {loading ? "Updating..." : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}